import { createFileRoute } from '@tanstack/react-router';
import type { CanvasserAnalyticsFilters } from '@/lib/queryKeys';
import { ShiftStatus } from '@signalvote/shared';
import { AnalyticsDashboard, CanvasserSelfView } from '@/components/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { ReadNotice } from '@/components/ui';
import { useCampaignStore } from '@/stores';
import { useMyStats, useShifts, useContactAnalytics } from '@/hooks/queries';

export const Route = createFileRoute('/app/analytics/')({
  component: AnalyticsDashboardRoute,
});

function AnalyticsDashboardRoute() {
  const filters = Route.useSearch() as CanvasserAnalyticsFilters;
  const { user } = useAuth();
  const { canView } = useCampaignStore();

  const hasAnalyticsCapability = canView('Analytics');

  if (hasAnalyticsCapability) {
    return <AnalyticsDashboard filters={filters} />;
  }

  return <SelfViewRoute userId={user?.id ?? ''} dateFrom={filters.dateFrom} dateTo={filters.dateTo} />;
}

function SelfViewRoute({ userId, dateFrom, dateTo }: { userId: string; dateFrom?: string; dateTo?: string }) {
  const dateFilters = { dateFrom, dateTo };
  const statsRead = useMyStats(dateFilters);
  const shifts = useShifts({
    claimedById: userId,
    status: ShiftStatus.Completed,
    startDate: dateFrom,
    endDate: dateTo,
  });
  const contactRead = useContactAnalytics(userId);

  return (
    <>
      <ReadNotice read={statsRead} subject="your stats" />
      <ReadNotice read={contactRead} subject="your contact activity" />
      <CanvasserSelfView
        myStats={statsRead.status === 'ready' ? statsRead.data : undefined}
        shifts={shifts}
        contactAnalytics={contactRead.status === 'ready' ? contactRead.data : undefined}
        currentUserId={userId}
        isLoadingStats={statsRead.status === 'loading' || statsRead.status === 'idle'}
        isLoadingContacts={contactRead.status === 'loading' || contactRead.status === 'idle'}
      />
    </>
  );
}
